import { createServerFn } from "@tanstack/react-start";

import { TOP_COUNT, listItem, type LocationListItem } from "./proxyLocations";
import type { ProxyLocation } from "./proxyLocations";

/**
 * What the pages read of the countries, as server functions: the route loaders
 * call them, and in the browser they are requests to the server. The countries
 * themselves are held by `locationCollection.ts`, imported inside each handler
 * so that it never reaches a browser chunk.
 */
async function collection() {
  const { countries } = await import("./locationCollection");
  return countries;
}

type LocationPageData = {
  country: ProxyLocation;
  /** Every country in the order, for the page's lists. */
  locations: Array<LocationListItem>;
};

/** A country's page, or null for a code the CMS does not hold, which the route 404s on. */
export const locationPageCms = createServerFn({ method: "GET" })
  .inputValidator((slug: unknown): string => String(slug ?? ""))
  .handler(async ({ data }): Promise<LocationPageData | null> => {
    const countries = await collection();
    const country = await countries.get(data);
    if (!country) return null;
    return { country, locations: (await countries.all()).map(listItem) };
  });

/** The head of the order, which the home page and pricing list. */
export const topLocationsCms = createServerFn({ method: "GET" }).handler(
  async (): Promise<Array<LocationListItem>> =>
    (await (await collection()).all()).slice(0, TOP_COUNT).map(listItem)
);

/**
 * Every country's IP count by its code, for the browser when it draws a
 * marketing page no server rendered for it: a client-side move onto one from a
 * dashboard page, which carries none.
 */
export const locationCountsCms = createServerFn({ method: "GET" }).handler(
  async (): Promise<Record<string, string>> => {
    const { locationCountsByCode } = await import("./locationCollection");
    return locationCountsByCode();
  }
);
